import React, { useEffect, useState } from 'react';
import { Table, Tag } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { MemberReportItem, ReportFilter } from '../../model/report';
import { fetchMemberReport } from '../../api/report';
import { exportExcel } from '../../utils/util';
import { useMeasuredRowScrollY, useTablePagination } from '../common/table-pagination';

interface Props {
    filter?: ReportFilter;
}

const ReportMember: React.FC<Props> = ({ filter }) => {
    const pagination = useTablePagination();
    const { ref: tableRef, y: scrollY } = useMeasuredRowScrollY(420);
    const [members, setMembers] = useState<MemberReportItem[]>([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        const load = async () => {
            setLoading(true);
            try {
                const res = await fetchMemberReport(filter);
                if (Array.isArray(res)) {
                    setMembers(res);
                }
            } catch (e) {
                console.error(e);
            }
            setLoading(false);
        };
        load();
    }, [filter]);

    const filteredMembers = members.filter(m =>
        m.member_code.toLowerCase().includes(searchTerm.toLowerCase()) ||
        m.member_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        m.phone.includes(searchTerm)
    );

    const totalMemberSpending = members.reduce((acc, m) => acc + m.total_spent, 0);
    const totalPoints = members.reduce((acc, m) => acc + m.points, 0);

    const memberColumns: ColumnsType<MemberReportItem> = [
        {
            title: 'อันดับ',
            key: 'rank',
            width: 70,
            render: (_: any, __: any, index: number) => (
                <span style={{ fontWeight: 700, color: index < 3 ? 'var(--primary, #FF7A59)' : '#64748B' }}>
                    #{index + 1}
                </span>
            )
        },
        { title: 'รหัสสมาชิก', dataIndex: 'member_code', key: 'member_code', width: 120 },
        {
            title: 'ชื่อ-นามสกุล',
            dataIndex: 'member_name',
            key: 'member_name',
            render: (val: string) => <span style={{ fontWeight: 600 }}>{val}</span>
        },
        { title: 'เบอร์โทร', dataIndex: 'phone', key: 'phone' },
        {
            title: 'กลุ่มสมาชิก',
            dataIndex: 'group_name',
            key: 'group_name',
            render: (val: string) => <Tag color="gold">{val}</Tag>
        },
        {
            title: 'ยอดซื้อสะสม (บาท)',
            dataIndex: 'total_spent',
            key: 'total_spent',
            render: (val: number) => <span style={{ fontWeight: 700, color: 'var(--primary, #FF7A59)' }}>฿{val.toLocaleString()}</span>
        },
        {
            title: 'จำนวนครั้งที่ซื้อ',
            dataIndex: 'total_visits',
            key: 'total_visits',
            render: (val: number) => `${val} ครั้ง`
        },
        {
            title: 'แต้มสะสม',
            dataIndex: 'points',
            key: 'points',
            render: (val: number) => <span style={{ fontWeight: 700, color: '#10B981' }}>{val.toLocaleString()} แต้ม</span>
        },
        { title: 'มาใช้บริการล่าสุด', dataIndex: 'last_visit', key: 'last_visit' }
    ];

    const handleExportExcel = () => {
        const exportData = filteredMembers.map((m, idx) => ({
            'อันดับ': idx + 1,
            'รหัสสมาชิก': m.member_code,
            'ชื่อ-นามสกุล': m.member_name,
            'เบอร์โทร': m.phone,
            'กลุ่มสมาชิก': m.group_name,
            'ยอดซื้อสะสม': m.total_spent,
            'จำนวนครั้งที่มา': m.total_visits,
            'แต้มสะสม': m.points,
            'มาใช้บริการล่าสุด': m.last_visit
        }));
        exportExcel('รายงานยอดซื้อสมาชิก', exportData);
    };

    return (
        <div>
            {/* Member KPI Cards */}
            <div className="report-kpi-grid">
                <div className="kpi-card">
                    <div className="kpi-icon-box kpi-sales">
                        <FontAwesomeIcon icon="users" />
                    </div>
                    <div className="kpi-info-box">
                        <span className="kpi-label">สมาชิกทั้งหมด</span>
                        <span className="kpi-value">{members.length} ท่าน</span>
                        <span className="kpi-subtext">ลูกค้าประจำของร้าน</span>
                    </div>
                </div>

                <div className="kpi-card">
                    <div className="kpi-icon-box kpi-profit">
                        <FontAwesomeIcon icon="credit-card" />
                    </div>
                    <div className="kpi-info-box">
                        <span className="kpi-label">ยอดซื้อรวมจากสมาชิก</span>
                        <span className="kpi-value" style={{ color: 'var(--primary, #FF7A59)' }}>฿{totalMemberSpending.toLocaleString()}</span>
                        <span className="kpi-subtext">เฉลี่ย ฿{Math.round(totalMemberSpending / (members.length || 1)).toLocaleString()} / ท่าน</span>
                    </div>
                </div>

                <div className="kpi-card">
                    <div className="kpi-icon-box kpi-items">
                        <FontAwesomeIcon icon="star" />
                    </div>
                    <div className="kpi-info-box">
                        <span className="kpi-label">แต้มสะสมคงเหลือรวม</span>
                        <span className="kpi-value" style={{ color: '#10B981' }}>{totalPoints.toLocaleString()} แต้ม</span>
                        <span className="kpi-subtext">สิทธิพิเศษ & ส่วนลด</span>
                    </div>
                </div>
            </div>

            {/* Member Table Section */}
            <div className="report-card-section">
                <div className="report-section-header">
                    <span className="report-section-title">
                        <FontAwesomeIcon icon="award" style={{ color: 'var(--primary, #FF7A59)' }} />
                        <span>สมาชิกลูกค้าประจำและยอดซื้อสะสม (Top Spending Members)</span>
                    </span>
                    <div style={{ display: 'flex', gap: '10px' }}>
                        <input
                            type="text"
                            className="report-search-input"
                            placeholder="ค้นหารหัส, ชื่อ หรือเบอร์โทร..."
                            value={searchTerm}
                            onChange={e => setSearchTerm(e.target.value)}
                        />
                        <button
                            type="button"
                            className="btn-report-action btn-excel"
                            onClick={handleExportExcel}
                        >
                            <FontAwesomeIcon icon="file-excel" /> ส่งออก Excel
                        </button>
                    </div>
                </div>

                <div ref={tableRef}>
                <Table
                    columns={memberColumns}
                    dataSource={filteredMembers}
                    rowKey="id"
                    pagination={pagination}
                    scroll={{ y: scrollY }}
                    loading={loading}
                />
                </div>
            </div>
        </div>
    );
};

export default ReportMember;
